import useStyles from "./styles";
import {
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Fade,
  Grid,
  TextField,
  Typography
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { Store, StoreWriteModel } from "../../../../models/Store";
import { StepProps } from "./types";
import { StepButtons } from "./components/StepButtons";
import * as storeApi from "../../../../api/storeApi";
import { isOk } from "../../../../result";
import { Language, TranslationV2 } from "../../../../models/Translation";
import * as TranslationsApi from "../../../../api/translationApi";
import { useSelector } from "react-redux";
import { AppState } from "../../../../store";
import { useLoadIfEmpty } from "../../../../hooks/useLoadIfEmpty";
import { thunkFetchCurrentTenantLanguages } from "../../../../store/tenants/actions";
import { getTranslationValuesOrDefault } from "../../../../utils";
import * as tenantApi from "../../../../api/tenantApi";
import * as StoreApi from "../../../../api/storeApi";
import * as TranslationApi from "../../../../api/translationApi";
import { Alert } from "@material-ui/lab";
import { TenantStatus } from "../../../../models/Tenant";
import { useI18n } from "../../../../hooks/useI18n";

export const StoreDetails = ({ onNext, onBack }: StepProps) => {
  const classes = useStyles();
  const { t } = useI18n();

  const tenantLanguages =
    useSelector<AppState, ReadonlyArray<Language>>(
      s => s.currentTenant.languages
    ) || [];

  useLoadIfEmpty(!tenantLanguages.length, thunkFetchCurrentTenantLanguages);

  const [store, setStore] = useState({} as Store);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({} as any);
  const [fatalError, setFatalError] = useState({} as any);
  const [translation, setTranslation] = useState<TranslationV2>({
    id: "",
    values: {}
  });

  useEffect(() => {
    setLoading(true);
    storeApi.list().then(result => {
      if (isOk(result)) {
        if (result.value.length >= 1) {
          setStore(result.value[0]);

          TranslationsApi.getById(`store.${result.value[0].id}.freetext`).then(
            result => {
              if (isOk(result)) {
                setTranslation(result.value);
              }
            }
          );
        }

        setLoading(false);
      }
    });
  }, []);

  const saveStoreInformation = async () => {
    setLoading(true);
    let result: any;
    result = await StoreApi.upsertOnboarding(store);

    if (result.type !== "OK") {
      setLoading(false);

      if (result.details.errors) {
        setValidationErrors(result.details.errors);
      }

      if (result.details.status !== 400) {
        setFatalError(result.details);
      }
      return;
    }

    if (translation.values) {
      setTranslation({ ...translation, id: `store.${result.value}.freetext` });
      let translationUpsertModel = { ...translation };
      translationUpsertModel.id = `store.${result.value}.freetext`;

      await TranslationApi.upsert(translationUpsertModel);
    }

    await tenantApi.updateTenantStatus(TenantStatus.OnBoardStep2Complete);
    setLoading(false);
    onNext();
  };

  return loading ? (
    <div
      style={{
        width: "100%",
        height: "100%",
        minHeight: "600px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <Fade
        in={true}
        style={{
          minHeight: 100,
          transitionDelay: "800ms"
        }}
        unmountOnExit
      >
        <div style={{ display: "flex", justifyContent: "center" }}>
          <CircularProgress size={80} />
        </div>
      </Fade>
    </div>
  ) : (
    <>
      <Typography variant={"h5"}>
        {t("common.messages.storeDetails")}
      </Typography>
      <Typography className={classes.onBoardingTitle} variant={"subtitle1"}>
        Please enter the store information below
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            required
            error={validationErrors["name"] != undefined}
            helperText={
              validationErrors["name"] != undefined
                ? validationErrors["name"]
                : ""
            }
            className={classes.gridField}
            label="Name"
            value={store.name ?? ""}
            onChange={e => {
              setValidationErrors({
                ...validationErrors,
                name: undefined
              });
              setStore({
                ...store,
                name: e.target.value
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            required
            error={validationErrors["address.Street"] != undefined}
            helperText={
              validationErrors["address.Street"] != undefined
                ? validationErrors["address.Street"]
                : ""
            }
            className={classes.gridField}
            label="Address"
            value={store.address?.street ?? ""}
            onChange={e => {
              setValidationErrors({
                ...validationErrors,
                ["address.Street"]: undefined
              });
              setStore({
                ...store,
                address: {
                  ...store.address,
                  street: e.target.value
                }
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            required
            error={validationErrors["address.PostalCode"] != undefined}
            helperText={
              validationErrors["address.PostalCode"] != undefined
                ? validationErrors["address.PostalCode"]
                : ""
            }
            className={classes.gridField}
            label={t("common.postalCode")}
            value={store.address?.postalCode ?? ""}
            onChange={e => {
              setValidationErrors({
                ...validationErrors,
                ["address.PostalCode"]: undefined
              });
              setStore({
                ...store,
                address: {
                  ...store.address,
                  postalCode: e.target.value
                }
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            required
            error={validationErrors["address.City"] != undefined}
            helperText={
              validationErrors["address.City"] != undefined
                ? validationErrors["address.City"]
                : ""
            }
            className={classes.gridField}
            label={t("common.city")}
            value={store.address?.city ?? ""}
            onChange={e => {
              setValidationErrors({
                ...validationErrors,
                ["address.City"]: undefined
              });
              setStore({
                ...store,
                address: {
                  ...store.address,
                  city: e.target.value
                }
              });
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Card
            variant={"outlined"}
            style={{ marginTop: 20, marginBottom: 20 }}
          >
            <CardHeader
              titleTypographyProps={{ variant: "h6" }}
              title={t("common.storeDescription")}
              subheader={t("common.describeYourStore")}
            />
            <CardContent>
              {tenantLanguages &&
                tenantLanguages.map(l => (
                  <React.Fragment key={l?.code}>
                    <TextField
                      key={l.code}
                      className={classes.field}
                      label={l.name}
                      value={
                        translation.values[l.code]
                          ? translation.values[l.code]
                          : ""
                      }
                      onChange={e => {
                        let translationValues =
                          getTranslationValuesOrDefault(translation);
                        translationValues[l.code] = e.target.value;
                        setTranslation({
                          ...translation,
                          values: translationValues
                        });
                      }}
                    />
                  </React.Fragment>
                ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {fatalError.title && (
        <Alert severity={"error"} title={fatalError.title}>
          {fatalError.detail}
        </Alert>
      )}
      <StepButtons
        nextDisabled={loading}
        backDisabled={loading}
        onClickBack={onBack}
        lastStep={true}
        onClickNext={() => saveStoreInformation()}
      />
    </>
  );
};
